import React, { useState } from 'react';
import {
  DocPage, PageHeader, Section, Prerequisites,
  Para, Callout,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './Plan.css';

const TOC = [
  { id: 'anatomy',       label: 'Anatomy of a plan'          },
  { id: 'behavior',      label: 'How plans differ from fees'  },
  { id: 'overage',       label: 'Overage'                     },
  { id: 'contract',      label: 'Plans in a contract'         },
  { id: 'carry-forward', label: 'Carry-forward'               },
  { id: 'examples',      label: 'Examples'                    },
];

// ── Plan pricing card ─────────────────────────────────────────────────────────

function PlanCard({ plan, currency }) {
  return (
    <div className="pl-card">
      <div className="pl-card-header">
        <span className="pl-card-name">{plan.name}</span>
        <div className="pl-card-price">
          <span className="pl-card-price-amount">{currency}{plan.rateCard.toLocaleString()}</span>
          <span className="pl-card-price-period">/ {plan.duration}</span>
        </div>
      </div>
      <div className="pl-card-fees">
        <div className="pl-card-fees-label">Plan grants</div>
        {plan.fees.map((fee) => (
          <div key={fee.name} className="pl-card-fee-row">
            <div className="pl-card-fee-name">{fee.name}</div>
            <div className="pl-card-fee-grant">
              {fee.perCycleGrantLimit
                ? <>{fee.perCycleGrantLimit.toLocaleString()} {fee.grant.unit} / {fee.billingCycle}</>
                : <>{fee.grant.amount.toLocaleString()} {fee.grant.unit}</>
              }
            </div>
            {fee.perCycleGrantLimit && (
              <div className="pl-card-fee-pool">
                {fee.grant.amount.toLocaleString()} {fee.grant.unit} total pool
              </div>
            )}
            <div className="pl-card-fee-overage">then {fee.overageRate}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Plan detail (field-level breakdown) ───────────────────────────────────────

function FieldRow({ label, value, highlight }) {
  return (
    <div className="pl-field-row">
      <span className="pl-field-label">{label}</span>
      <span className={`pl-field-value${highlight ? ' pl-field-value--highlight' : ''}`}>{value}</span>
    </div>
  );
}

function PlanDetail({ plan, currency }) {
  return (
    <div className="pl-detail-card">
      <div className="pl-detail-header">
        <span className="pl-detail-name">{plan.name}</span>
      </div>
      <div className="pl-detail-section">
        <FieldRow label="Rate card" value={`${currency}${plan.rateCard.toLocaleString()} (fixed)`} highlight />
        <FieldRow label="Duration"  value={plan.duration} />
      </div>
      {plan.fees.map((fee) => (
        <div key={fee.name} className="pl-detail-fee-block">
          <div className="pl-detail-fee-header">
            <span className="pl-detail-fee-product">{fee.product}</span>
            <span className="pl-detail-fee-arrow">›</span>
            <span className="pl-detail-fee-name">{fee.name}</span>
          </div>
          <div className="pl-detail-section pl-detail-section--fee">
            <FieldRow label="Payment mode"      value={fee.paymentMode} />
            <FieldRow label="Starts at"         value={fee.startsAt} />
            <FieldRow label="Duration"          value={fee.duration} />
            <FieldRow label="Billing cycle"     value={fee.billingCycle} />
            <FieldRow label="Plan grant"
              value={`${fee.grant.type} · ${fee.grant.amount.toLocaleString()} ${fee.grant.unit}`}
              highlight
            />
            <FieldRow label="Overage rate card" value={fee.overageRate} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Overage calculator ────────────────────────────────────────────────────────

function trackBg(used, included, max) {
  const pI = (included / max) * 100;
  const pU = Math.min((used / max) * 100, 100);
  if (used <= included) {
    return `linear-gradient(to right, var(--teal-400) 0% ${pU}%, var(--gray-200) ${pU}% 100%)`;
  }
  return `linear-gradient(to right, var(--teal-400) 0% ${pI}%, var(--amber-400) ${pI}% ${pU}%, var(--gray-200) ${pU}% 100%)`;
}

function OverageCalculator({ plan, currency }) {
  const [usage, setUsage] = useState(
    Object.fromEntries(plan.fees.map((f) => [f.name, f.perCycleGrantLimit ?? f.grant.amount]))
  );

  const rows = plan.fees.map((fee) => {
    const included = fee.perCycleGrantLimit ?? fee.grant.amount;
    const used     = Number(usage[fee.name]) || 0;
    const overage  = Math.max(0, used - included);
    const charge   = overage * (fee.overageRateNum || 0);
    return { fee, included, used, overage, charge };
  });

  const planCharge = plan.rateCard;
  const overageTotal = rows.reduce((s, r) => s + r.charge, 0);
  const total = planCharge + overageTotal;

  const fmt = (n) => {
    const rounded = Math.round(n * 100) / 100;
    return rounded % 1 === 0 ? rounded.toLocaleString() : rounded.toLocaleString(undefined, { minimumFractionDigits: 2 });
  };

  return (
    <div className="pl-calc">
      <div className="pl-calc-inputs">
        {rows.map(({ fee, included }) => {
          const used = Number(usage[fee.name]) || 0;
          const max  = included * 2;
          return (
            <div key={fee.name} className="pl-calc-input-row">
              <div className="pl-calc-slider-header">
                <span className="pl-calc-label">{fee.name}</span>
                <span className={`pl-calc-slider-val${used > included ? ' pl-calc-slider-val--over' : ''}`}>
                  {used.toLocaleString()} <span className="pl-calc-unit">{fee.grant.unit}</span>
                </span>
              </div>
              <input
                type="range"
                className="pl-calc-slider"
                min={0}
                max={max}
                step={1}
                value={used}
                style={{ background: trackBg(used, included, max) }}
                onChange={(e) => setUsage({ ...usage, [fee.name]: e.target.value })}
              />
              <span className="pl-calc-label-hint">
                {included.toLocaleString()} {fee.grant.unit} included this {fee.billingCycle ?? 'period'}
                {fee.perCycleGrantLimit && ` · ${fee.grant.amount.toLocaleString()} total pool`}
                {' · '}{fee.overageRate} after
              </span>
            </div>
          );
        })}
      </div>

      <div className="pl-calc-result">
        <div className="pl-calc-result-row pl-calc-result-row--plan">
          <span className="pl-calc-result-label">Plan charge</span>
          <span className="pl-calc-result-note">{plan.name} · flat</span>
          <span className="pl-calc-result-amount">{currency}{fmt(planCharge)}</span>
        </div>
        {rows.map(({ fee, included, used, overage, charge }) => (
          <React.Fragment key={fee.name}>
            <div className="pl-calc-result-row">
              <span className="pl-calc-result-label">{fee.name} · included</span>
              <span className="pl-calc-result-note">
                {Math.min(used, included).toLocaleString()} {fee.grant.unit} × {currency}0
              </span>
              <span className="pl-calc-result-amount pl-calc-result-zero">{currency}0</span>
            </div>
            {overage > 0 && (
              <div className="pl-calc-result-row pl-calc-result-row--overage">
                <span className="pl-calc-result-label">{fee.name} · overage</span>
                <span className="pl-calc-result-note">
                  {overage.toLocaleString()} × {fee.overageRate.split(' ').slice(-1)[0]}
                </span>
                <span className="pl-calc-result-amount">{currency}{fmt(charge)}</span>
              </div>
            )}
          </React.Fragment>
        ))}
        <div className="pl-calc-result-row pl-calc-result-row--total">
          <span className="pl-calc-result-label">Total</span>
          <span className="pl-calc-result-note" />
          <span className="pl-calc-result-amount">{currency}{fmt(total)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Carry-forward calculator ──────────────────────────────────────────────────

const NUM_PERIODS = 4;

function CarryForwardCalculator({ cf, currency, overageRateNum }) {
  const [usages, setUsages] = useState(
    cf.cycles.map((c) => c.used).concat(
      Array(Math.max(0, NUM_PERIODS - cf.cycles.length)).fill(cf.included)
    ).slice(0, NUM_PERIODS)
  );

  // Two-pass: need prev carried before computing current row
  const rows = [];
  let prevCarried = 0;
  for (let i = 0; i < NUM_PERIODS; i++) {
    const used      = Number(usages[i]) || 0;
    const available = cf.included + prevCarried;
    const carriedOut = Math.max(0, available - used);
    const overage   = Math.max(0, used - available);
    rows.push({ label: `Period ${i + 1}`, used, available, carriedOut, overage, prevCarried });
    prevCarried = carriedOut;
  }

  const fmt = (n) => (Math.round(n * 100) / 100).toLocaleString();

  return (
    <div className="pl-cf-calc">
      <div className="pl-cf-calc-inputs">
        {rows.map((row, i) => {
          const used = Number(usages[i]) || 0;
          const max  = Math.max(row.available * 2, cf.included * 2);
          return (
            <div key={i} className="pl-cf-calc-input-row">
              <div className="pl-calc-slider-header">
                <span className="pl-calc-label">{row.label}</span>
                <span className={`pl-calc-slider-val${used > row.available ? ' pl-calc-slider-val--over' : ''}`}>
                  {used.toLocaleString()} <span className="pl-calc-unit">{cf.unit}</span>
                </span>
              </div>
              <input
                type="range"
                className="pl-calc-slider"
                min={0}
                max={max}
                step={1}
                value={used}
                style={{ background: trackBg(used, row.available, max) }}
                onChange={(e) => {
                  const next = [...usages];
                  next[i] = e.target.value;
                  setUsages(next);
                }}
              />
              <span className="pl-calc-label-hint">
                {cf.included.toLocaleString()} fresh
                {row.prevCarried > 0 ? ` + ${row.prevCarried.toLocaleString()} carried` : ''}
                {' '}= {row.available.toLocaleString()} available
              </span>
            </div>
          );
        })}
      </div>

      <div className="pl-cf-wrap">
        <table className="pl-cf-table">
          <thead>
            <tr>
              <th>Period</th>
              <th>Fresh</th>
              <th>Carried in</th>
              <th>Available</th>
              <th>Used</th>
              <th>Carried out</th>
              <th>Overage</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td className="pl-cf-period">{row.label}</td>
                <td>{cf.included.toLocaleString()}</td>
                <td className={row.prevCarried > 0 ? 'pl-cf-carried' : 'pl-cf-zero'}>{fmt(row.prevCarried)}</td>
                <td className="pl-cf-avail">{fmt(row.available)}</td>
                <td>{fmt(row.used)}</td>
                <td className={row.carriedOut > 0 ? 'pl-cf-carried' : 'pl-cf-zero'}>{fmt(row.carriedOut)}</td>
                <td className={row.overage > 0 ? 'pl-cf-overage' : 'pl-cf-zero'}>
                  {row.overage > 0
                    ? `${fmt(row.overage)} × ${currency}${overageRateNum} = ${currency}${fmt(row.overage * overageRateNum)}`
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Limited carry-forward calculator (multi-fee, CF expires after N periods) ─

function LimitedCFCalculator({ config, currency }) {
  const NUM_DISPLAY = config.cfUntilPeriod + 2; // show CF window + 2 periods after cutoff

  const [usages, setUsages] = useState(
    Object.fromEntries(config.fees.map((fee) => [
      fee.name,
      (config.defaultUsages?.[fee.name] || Array(NUM_DISPLAY).fill(fee.included)).slice(0, NUM_DISPLAY),
    ]))
  );

  // Two-pass per fee: carry chain is sequential
  const feeData = config.fees.map((fee) => {
    const rows = [];
    let carried = 0;
    for (let i = 0; i < NUM_DISPLAY; i++) {
      const cfActive   = i < config.cfUntilPeriod;
      const used       = Number(usages[fee.name][i]) || 0;
      const available  = fee.included + carried;
      const carriedOut = cfActive ? Math.max(0, available - used) : 0;
      const overage    = Math.max(0, used - available);
      rows.push({ period: i + 1, used, available, carried, carriedOut, overage, cfActive });
      carried = cfActive ? carriedOut : 0;
    }
    return { fee, rows };
  });

  const fmt = (n) => (Math.round(n * 100) / 100).toLocaleString();

  return (
    <div className="pl-lcf-calc">
      {/* ── Input grid ── */}
      <div className="pl-lcf-inputs">
        {Array.from({ length: NUM_DISPLAY }, (_, i) => {
          const isBoundary = i === config.cfUntilPeriod;
          const cfActive   = i < config.cfUntilPeriod;
          return (
            <React.Fragment key={i}>
              {isBoundary && (
                <div className="pl-lcf-boundary">
                  <span>Carry-forward stops — month {config.cfUntilPeriod + 1} onwards</span>
                </div>
              )}
              <div className={`pl-lcf-period${cfActive ? ' pl-lcf-period--cf' : ' pl-lcf-period--nocf'}`}>
                <div className="pl-lcf-period-header">
                  <span className="pl-lcf-period-label">Month {i + 1}</span>
                  {cfActive
                    ? <span className="pl-lcf-badge pl-lcf-badge--cf">carry-forward on</span>
                    : <span className="pl-lcf-badge pl-lcf-badge--off">expires at month end</span>}
                </div>
                {config.fees.map((fee, fi) => {
                  const row  = feeData[fi].rows[i];
                  const used = Number(usages[fee.name][i]) || 0;
                  const max  = fee.included * 2;
                  return (
                    <div key={fee.name} className="pl-calc-input-row">
                      <div className="pl-calc-slider-header">
                        <span className="pl-calc-label">{fee.name}</span>
                        <span className={`pl-calc-slider-val${used > row.available ? ' pl-calc-slider-val--over' : ''}`}>
                          {used} <span className="pl-calc-unit">{fee.unit}</span>
                        </span>
                      </div>
                      <input
                        type="range"
                        className="pl-calc-slider"
                        min={0}
                        max={max}
                        step={1}
                        value={used}
                        style={{ background: trackBg(used, row.available, max) }}
                        onChange={(e) => {
                          const next = [...usages[fee.name]];
                          next[i] = e.target.value;
                          setUsages({ ...usages, [fee.name]: next });
                        }}
                      />
                      <span className="pl-calc-label-hint">
                        {fee.included} fresh
                        {row.carried > 0 ? ` + ${fmt(row.carried)} carried` : ''}
                        {' '}= {fmt(row.available)} available
                      </span>
                    </div>
                  );
                })}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Results table per fee ── */}
      {feeData.map(({ fee, rows }) => (
        <div key={fee.name}>
          <div className="pl-lcf-table-label">{fee.name}</div>
          <div className="pl-cf-wrap pl-lcf-table-wrap">
            <table className="pl-cf-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Fresh</th>
                  <th>Carried in</th>
                  <th>Available</th>
                  <th>Used</th>
                  <th>Carried out</th>
                  <th>Overage</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.period} className={!row.cfActive ? 'pl-lcf-row-nocf' : ''}>
                    <td className="pl-cf-period">M{row.period}</td>
                    <td>{fee.included}</td>
                    <td className={row.carried > 0 ? 'pl-cf-carried' : 'pl-cf-zero'}>{fmt(row.carried)}</td>
                    <td className="pl-cf-avail">{fmt(row.available)}</td>
                    <td>{fmt(row.used)}</td>
                    <td className={row.carriedOut > 0 ? 'pl-cf-carried' : (!row.cfActive ? 'pl-cf-zero' : 'pl-cf-zero')}>
                      {row.cfActive ? fmt(row.carriedOut) : <span className="pl-lcf-expired">—</span>}
                    </td>
                    <td className={row.overage > 0 ? 'pl-cf-overage' : 'pl-cf-zero'}>
                      {row.overage > 0
                        ? `${fmt(row.overage)} × ${currency}${fee.overageRateNum} = ${currency}${fmt(row.overage * fee.overageRateNum)}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Example carousel ─────────────────────────────────────────────────────────

function ExampleCarousel({ examples }) {
  const [idx, setIdx] = useState(0);
  const ex = examples[idx];

  return (
    <div className="pl-ex-carousel">
      <div className="pl-ex-nav">
        <button
          className="pl-ex-arrow"
          onClick={() => setIdx((i) => i - 1)}
          disabled={idx === 0}
          aria-label="Previous example"
        >
          &#8592;
        </button>
        <div className="pl-ex-meta">
          <span className="pl-ex-counter">Example {idx + 1} of {examples.length}</span>
          <span className="pl-ex-title">{ex.title}</span>
        </div>
        <button
          className="pl-ex-arrow"
          onClick={() => setIdx((i) => i + 1)}
          disabled={idx === examples.length - 1}
          aria-label="Next example"
        >
          &#8594;
        </button>
      </div>

      <div className="pl-ex-dots">
        {examples.map((_, i) => (
          <button
            key={i}
            className={`pl-ex-dot${i === idx ? ' pl-ex-dot--active' : ''}`}
            onClick={() => setIdx(i)}
            aria-label={`Example ${i + 1}`}
          />
        ))}
      </div>

      <div className="pl-ex-body">
        <div className="pl-ex-scenario">{ex.scenario}</div>
        {ex.calc}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Plan({ onNavigate }) {
  const { industry } = useIndustry();
  const pl = industry.plan;

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Catalog Setup"
        title="Plans"
        description="A Plan is a pre-packaged pricing offer: a fixed recurring charge bundled with fees that carry plan grants (included quantities). When a grant is exhausted, the fee's rate card takes over."
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'fee',      label: 'Fees'       },
          { id: 'grant',    label: 'Grants'     },
          { id: 'contract', label: 'Contracts'  },
        ]}
      />

      <Section id="anatomy" title="Anatomy of a plan">
        <Para>
          A plan has a <strong>rate card</strong> (the flat charge billed every period) and a
          set of <strong>fees with plan grants</strong> — each fee carries an included quantity
          that is zero-rated before its overage rate card kicks in.
        </Para>
        <div className="pl-cards">
          {pl.plans.map((plan) => (
            <PlanCard key={plan.name} plan={plan} currency={pl.currency} />
          ))}
        </div>
        <Callout type="info">
          Plans are defined in the catalog with default pricing. When you attach a plan to a
          contract, the defaults carry over — but any field (rate card, plan grant, overage
          rate, billing cycle) can be overridden per contract.
        </Callout>
      </Section>

      <Section id="behavior" title="How plans differ from fees">
        <Para>
          A plan is essentially a bundle — one flat charge covering multiple products.
          Fees are single-product and usage-only.
        </Para>
        <div className="pl-diff-table">
          <div className="pl-diff-row pl-diff-row--header">
            <span>Attribute</span>
            <span>Fee</span>
            <span>Plan</span>
          </div>
          <div className="pl-diff-row">
            <span>Products</span>
            <span>One</span>
            <span className="pl-diff-highlight">Multiple</span>
          </div>
          <div className="pl-diff-row">
            <span>Charge</span>
            <span>Usage-based</span>
            <span className="pl-diff-highlight">Flat recurring + overage</span>
          </div>
        </div>
      </Section>

      <Section id="overage" title="Overage">
        <Para>
          The plan grant on each fee zero-rates usage up to the included amount. When the plan
          grant is exhausted, the fee's rate card takes over and bills the remaining usage.
          The plan's fixed charge is always billed in full — it does not adjust based on usage.
        </Para>
        <Callout type="tip">
          Each fee's plan grant is tracked independently. Exhausting one fee's grant does not
          affect any other fee in the plan.
        </Callout>
      </Section>

      <Section id="contract" title="Plans in a contract">
        <Para>
          A contract can include one or more plans simultaneously, alongside any number of
          ad-hoc fees. Plans contribute a fixed charge plus any overage charges to the invoice.
          Ad-hoc fees are billed independently. All charges appear on the same invoice.
        </Para>
        <Callout type="info">
          Multiple plans on one contract are tracked independently — each plan's fixed charge,
          plan grants, and overages are calculated separately before appearing on the same invoice.
        </Callout>
      </Section>

      <Section id="carry-forward" title="Carry-forward">
        <Para>
          By default, unused plan grant quantities expire at the end of the billing period.
          When carry-forward is enabled, unused quantities roll into the next period and stack
          on top of the fresh grant. Overage is only charged once the total available
          balance — carried-in plus freshly granted — is consumed.
        </Para>
        <Callout type="info">
          Carry-forward is configured per fee within the plan. You can enable it on some fees
          and leave others expiring at period end.
        </Callout>
      </Section>

      <Section id="examples" title="Examples">
        <ExampleCarousel examples={[
          {
            title: 'Going over your included allowance',
            scenario: (
              <p className="pl-ex-scenario-text">
                You're on the <strong>{pl.plans[0].name}</strong> — a flat{' '}
                <strong>{pl.currency}{pl.plans[0].rateCard.toLocaleString()}/{pl.plans[0].duration}</strong> that
                covers{' '}
                {pl.plans[0].fees.map((f, i) => (
                  <span key={f.name}>
                    <strong>{f.grant.amount.toLocaleString()} {f.grant.unit}</strong>
                    {i < pl.plans[0].fees.length - 1 ? ' and ' : ''}
                  </span>
                ))}.{' '}
                The flat charge is fixed — it doesn't change whether you use everything or nothing.
                But usage beyond the included amount gets billed at the overage rate.
                Drag past the included mark to see the extra charges appear.
              </p>
            ),
            calc: <OverageCalculator plan={pl.plans[0]} currency={pl.currency} />,
          },
          ...(pl.plans[1] ? [{
            title: 'Monthly cap on an annual plan',
            scenario: (
              <p className="pl-ex-scenario-text">
                Same <strong>{pl.currency}{pl.plans[1].rateCard.toLocaleString()}/year</strong> as
                before, but now the grant resets every month instead of sitting in one annual pool.
                Each billing cycle you get a fresh{' '}
                {pl.plans[1].fees.map((f, i) => (
                  <span key={f.name}>
                    <strong>{f.perCycleGrantLimit} {f.grant.unit}</strong>
                    {i < pl.plans[1].fees.length - 1 ? ' and ' : ''}
                  </span>
                ))}.{' '}
                Unused amounts don't roll to the next month (unless carry-forward is on),
                and you can't borrow ahead — if you send 130 emails in a slow month,
                the extra 30 still incur overage even though you're well within your annual total.
              </p>
            ),
            calc: <OverageCalculator plan={pl.plans[1]} currency={pl.currency} />,
          }] : []),
          ...(pl.carryForwardLimited ? [{
            title: 'Carry-forward that expires mid-way',
            scenario: (
              <p className="pl-ex-scenario-text">
                Each period you get a fresh allowance of{' '}
                {pl.carryForwardLimited.fees.map((f, i) => (
                  <span key={f.name}>
                    <strong>{f.included.toLocaleString()} {f.unit}</strong>
                    {i < pl.carryForwardLimited.fees.length - 1 ? ' and ' : ''}
                  </span>
                ))}.{' '}
                For the first <strong>{pl.carryForwardLimited.cfUntilPeriod} months</strong>,
                anything unused rolls into the next period — quiet months build a buffer, busy
                months draw from it. But at the end of month {pl.carryForwardLimited.cfUntilPeriod},
                whatever's left is gone. From month {pl.carryForwardLimited.cfUntilPeriod + 1}
                onward, unused allowance expires at period end. Try making early months light to
                watch the buffer grow — then see it vanish at the cutoff.
              </p>
            ),
            calc: <LimitedCFCalculator config={pl.carryForwardLimited} currency={pl.currency} />,
          }] : []),
          {
            title: 'Unused allowance rolls into next period',
            scenario: (
              <p className="pl-ex-scenario-text">
                With carry-forward on, the{' '}
                <strong>{pl.carryForward.fee}</strong> grant doesn't expire at period end.
                Whatever you don't use stacks on top of next period's fresh{' '}
                <strong>{pl.carryForward.included.toLocaleString()} {pl.carryForward.unit}</strong>.
                A quiet period builds a cushion; a busy one draws it down.
                Overage only fires when the combined balance — fresh plus carried — runs out.
                Try making one period very light, then spike the next.
              </p>
            ),
            calc: (
              <CarryForwardCalculator
                cf={pl.carryForward}
                currency={pl.currency}
                overageRateNum={pl.plans[0].fees.find((f) => f.name === pl.carryForward.fee)?.overageRateNum || 0}
              />
            ),
          },
        ]} />
      </Section>

    </DocPage>
  );
}
